import * as Ramda from 'ramda';
import {
  CANCELLED_STATUS,
  EXPIRED_STATUS,
  RENEWED_STATUS,
} from '../../../../../console/_statics/policy.statics';
import { isZurichProduct, ProductTypes } from '../../../../../types';

import { VOID_STATUS } from '../../../../../console/_statics/quote.statics';
import PoliciesService from '../../../../../console/policies/policies.service';
import type { PolicyTableInfoDto } from '../../../../../types/policy/tables/policy-table-info.dto';
import { determineIfAllowedScopes } from '../../../../../utils/next/auth.utils';
import type { Permissions } from '../../../../auth/permissions';

interface IOptions {
  flags: object;
}

export const getPolicyActionsForCarrier = (
  policy: PolicyTableInfoDto,
  scopes: Permissions[],
  options: IOptions = { flags: {} }
) => {
  const canDownloadDocs =
    PoliciesService.isActivePolicyByStatus(policy) ||
    [RENEWED_STATUS, CANCELLED_STATUS, EXPIRED_STATUS].includes(policy.status);

  const CAN_APPLY_PAYMENT =
    [
      ProductTypes.prime_one,
      ProductTypes.p_one_plus,
      ProductTypes.prime_one_tech_plus,
    ].some(Ramda.equals(policy.product)) &&
    determineIfAllowedScopes(
      scopes,
      ['account:premium_processor', 'policy:apply_payment'],
      true
    );

  const CAN_DOWNLOAD_APPLICATION =
    !policy.isPrimePlus &&
    ![ProductTypes.prime_tech_plus].some(Ramda.equals(policy.product)) &&
    canDownloadDocs;

  const CAN_DOWNLOAD_INVOICE =
    determineIfAllowedScopes(scopes, [
      'account:premium_processor',
      'account:cowbell',
    ]) &&
    (policy.isInvoiceAvailable || policy.isDocumentAvailable) &&
    !PoliciesService.isNonPremiumBearingEndorsement(policy);

  const CAN_DOWNLOAD_POLICY_DOCUMENTS =
    PoliciesService.isActivePolicyByStatus(policy) ||
    [RENEWED_STATUS, EXPIRED_STATUS].includes(policy.status);

  const CAN_VIEW_RATER = (() => {
    const isPremiumProcessor = determineIfAllowedScopes(scopes, [
      'account:premium_processor',
    ]);
    const isReinsurer = determineIfAllowedScopes(scopes, [
      'account:re_insurer',
    ]);

    const isEligibleByStatus =
      [RENEWED_STATUS, EXPIRED_STATUS, VOID_STATUS, CANCELLED_STATUS].includes(
        policy.status
      ) || PoliciesService.isActivePolicyByStatus(policy);

    const isEligibleByProduct = [
      ProductTypes.p250,
      ProductTypes.prime_one,
    ].some(Ramda.equals(policy.product));

    if (policy.isPrimePlus || policy.isEndorsement || isPremiumProcessor) {
      return false;
    }

    if (isReinsurer && policy.raterXCompatible) {
      return false;
    }

    if (
      (policy.raterXCompatible || isEligibleByProduct) &&
      isEligibleByStatus
    ) {
      return true;
    }

    return false;
  })();

  const CAN_DOWNLOAD_LOSS_RUNS = (() => {
    const isPremiumProcessor = determineIfAllowedScopes(scopes, [
      'account:premium_processor',
    ]);

    const isReinsurer = determineIfAllowedScopes(scopes, [
      'account:re_insurer',
    ]);

    if (isPremiumProcessor || isReinsurer) {
      return false;
    }

    return isZurichProduct(policy.product);
  })();

  const CAN_OPTOUT_RENEWAL_PROCESS =
    isZurichProduct(policy.product) &&
    PoliciesService.isActivePolicyByStatus(policy) &&
    !policy.isEndorsement;

  return {
    CAN_APPLY_PAYMENT,
    CAN_DOWNLOAD_APPLICATION,
    CAN_DOWNLOAD_INVOICE,
    CAN_DOWNLOAD_POLICY_DOCUMENTS,
    CAN_VIEW_RATER,
    CAN_DOWNLOAD_LOSS_RUNS,
    CAN_OPTOUT_RENEWAL_PROCESS,
  };
};
